"use client";

import Link from "next/link";
import {
  BadgeCheck,
  Mail,
  MessageCircle,
  Phone,
  Sparkles,
  Star,
  UserPlus,
} from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { normalizeAgentAvatarSrc } from "~/lib/agent-avatar-url";
import { type MoonlightAgentListItem } from "~/lib/marketplace/moonlight-home";
import {
  marketplaceIndustryLabel,
  marketplaceRoleLabel,
} from "~/lib/marketplace/constants";

type MoonlightAgentCardProps = {
  agent: MoonlightAgentListItem;
  /** Hire/custom flow: wire Live Demo and Clone to parent handlers instead of navigation. */
  hireFlowMode?: boolean;
  onLiveDemo?: (agent: MoonlightAgentListItem) => void;
  onClone?: (agent: MoonlightAgentListItem) => void;
};

function formatPhoneDisplay(phone: string | null | undefined): string | null {
  if (!phone?.trim()) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone.trim();
}

const LIVE_DEMO_BUTTON_CLASS =
  "border-[#4f81bd] bg-[#4f81bd] text-white hover:bg-[#416da3] hover:text-white";

function agentFirstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

export function MoonlightAgentCard({
  agent,
  hireFlowMode = false,
  onLiveDemo,
  onClone,
}: MoonlightAgentCardProps) {
  const name = agent.aiAgent?.name ?? agent.title;
  const cloneLabel = `Clone ${agentFirstName(name)}`;
  const company = agent.title;
  const jobTitle =
    agent.aiAgent?.jobTitle ||
    marketplaceRoleLabel(agent.role) ||
    "AI inbound specialist";
  const email = agent.aiAgent?.emailAddress?.trim() || null;
  const phone = formatPhoneDisplay(agent.aiAgent?.phoneNumber);
  const isMoonlight = agent.listingType === "MOONLIGHT";
  const isMarketplaceListed =
    agent.listingType === "OFFICIAL" || agent.listingType === "MOONLIGHT";
  const avatarSrc =
    normalizeAgentAvatarSrc(agent.aiAgent?.base64Avatar) ??
    normalizeAgentAvatarSrc(agent.videoThumbnailUrl);
  const responsibilities = agent.responsibilities;
  const tools = agent.tools.slice(0, 4);

  const tryMeHref = `${agent.profilePath}?try=1`;

  const listingBadge = isMoonlight ? (
    <Badge className="absolute right-2 top-2 gap-1 border-0 bg-primary/95 text-primary-foreground shadow-sm">
      <Sparkles className="h-3 w-3" />
      Moonlight
    </Badge>
  ) : (
    <Badge className="absolute right-2 top-2 gap-1 border-0 bg-white/95 text-slate-800 shadow-sm">
      <BadgeCheck className="h-3 w-3 text-primary" />
      Official
    </Badge>
  );

  const avatarFallback = (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 text-4xl font-bold text-slate-500">
      {name
        .split(" ")
        .map((p) => p[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()}
    </div>
  );

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <Link href={agent.profilePath} className="block">
        <div className="relative flex justify-center bg-slate-50 px-4 py-6 dark:bg-slate-800/50">
          <div className="relative h-28 w-28 overflow-hidden rounded-full border-4 border-white shadow-md dark:border-slate-700">
            {avatarSrc ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={avatarSrc}
                alt={name}
                className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
              />
            ) : (
              avatarFallback
            )}
          </div>
          {listingBadge}
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4 pt-3">
        <div className="flex min-h-0 flex-1 flex-col gap-2.5">
          <Link href={agent.profilePath} className="space-y-0.5 hover:opacity-90">
            <h3 className="line-clamp-1 text-base font-semibold text-slate-900 dark:text-white">
              {name}
            </h3>
            <p className="line-clamp-1 text-sm text-slate-600 dark:text-slate-300">
              {jobTitle}
            </p>
            <p className="line-clamp-1 text-sm font-medium text-slate-800 dark:text-slate-200">
              {company}
            </p>
          </Link>

          <div className="space-y-1 text-xs text-slate-500">
            {email && (
              <div className="flex min-w-0 items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate">{email}</span>
              </div>
            )}
            {phone && (
              <div className="flex min-w-0 items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate">{phone}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {responsibilities.length > 0 ? (
              <div className="flex min-h-[26px] flex-wrap content-start gap-1.5">
                {responsibilities.map((label) => (
                  <Badge
                    key={label}
                    variant="secondary"
                    className="rounded-sm px-2 py-0.5 text-[11px] font-normal"
                  >
                    {label}
                  </Badge>
                ))}
              </div>
            ) : null}
            {tools.length > 0 ? (
              <div className="flex flex-wrap content-start gap-1.5">
                {tools.map((label) => (
                  <Badge
                    key={label}
                    variant="outline"
                    className="rounded-sm border-primary/25 bg-primary/5 px-2 py-0.5 text-[11px] font-normal text-primary"
                  >
                    {label}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-auto flex flex-col gap-3 pt-3">
          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              className={`h-9 w-full text-xs ${LIVE_DEMO_BUTTON_CLASS}`}
              {...(hireFlowMode && onLiveDemo
                ? {
                    type: "button" as const,
                    onClick: () => onLiveDemo(agent),
                  }
                : { asChild: true })}
            >
              {hireFlowMode && onLiveDemo ? (
                <>
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                  Live Demo
                </>
              ) : (
                <Link href={tryMeHref}>
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                  Live Demo
                </Link>
              )}
            </Button>
            {isMarketplaceListed && (
              <Button
                className="h-9 w-full bg-slate-900 text-xs font-semibold hover:bg-slate-800"
                {...(hireFlowMode && onClone
                  ? {
                      type: "button" as const,
                      onClick: () => onClone(agent),
                    }
                  : { asChild: true })}
              >
                {hireFlowMode && onClone ? (
                  <>
                    <UserPlus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    {cloneLabel}
                  </>
                ) : (
                  <Link href={`/agents/${agent.id}?hire=1`}>
                    <UserPlus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    {cloneLabel}
                  </Link>
                )}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-1 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800">
            <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
            <span className="truncate font-medium text-slate-700 dark:text-slate-200">
              {agent.featured ? "Top performer" : "Verified"}
            </span>
            <span className="hidden sm:inline">
              · {marketplaceIndustryLabel(agent.industry)}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}
